import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Logo } from "@/components/site/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const usePaymentStatus = (sessionId) => {
  const [state, setState] = useState(sessionId ? "checking" : "missing");
  useEffect(() => {
    if (!sessionId) return undefined;
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data?.payment_status === "paid") return setState("paid");
        if (["expired", "failed"].includes(data?.payment_status)) return setState("failed");
      } catch (e) {
        if (e?.response?.status === 404) return setState("failed");
      }
      tries += 1;
      if (tries < 15) timer = setTimeout(poll, 2000);
      else setState("pending");
      return undefined;
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);
  return state;
};

const COPY = {
  checking: { title: "Confirming your payment", text: "This only takes a moment." },
  paid: { title: "Thank you, your kaba is yours.", text: "Your payment of $125 was received. A confirmation email is on its way, and we’ll reach out to arrange delivery." },
  pending: { title: "Payment still processing", text: "We haven’t received confirmation yet. Check your email shortly, or contact us if you were charged." },
  failed: { title: "Payment not completed", text: "Your card was not charged. You can go back and try again." },
  missing: { title: "Payment not completed", text: "Your card was not charged. You can go back and try again." },
  cancel: { title: "Payment cancelled", text: "No problem — your card was not charged. Your order form is waiting whenever you’re ready." },
};

export default function PaymentResult({ cancelled = false }) {
  const { search } = useLocation();
  const sessionId = new URLSearchParams(search).get("session_id");
  const status = usePaymentStatus(cancelled ? null : sessionId);
  const key = cancelled ? "cancel" : status;
  const Icon = key === "paid" ? CheckCircle2 : key === "checking" ? Loader2 : XCircle;
  return (
    <div className="flex min-h-screen flex-col bg-ink font-body text-cream">
      <header className="px-5 py-5 sm:px-10"><Link to="/" data-testid="payment-home-logo"><Logo /></Link></header>
      <main className="mx-auto flex max-w-xl flex-1 flex-col items-start justify-center px-5 pb-24 sm:px-10" data-testid={`payment-${key}`}>
        <Icon className={`h-12 w-12 ${key === "paid" ? "text-white" : "text-cream"} ${key === "checking" ? "animate-spin" : ""}`} />
        <h1 className="mt-8 font-display text-4xl font-light leading-tight sm:text-5xl" data-testid="payment-title">{COPY[key].title}</h1>
        <p className="mt-5 text-base font-light leading-relaxed text-stone2 sm:text-lg">{COPY[key].text}</p>
        <Link to={key === "paid" ? "/" : "/#order"} data-testid="payment-back-link"
          className="mt-10 rounded-full bg-cream px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-transform duration-300 hover:-translate-y-0.5">
          {key === "paid" ? "Back to Hilmay’s Kabas" : "Back to the order form"}
        </Link>
      </main>
    </div>
  );
}
